import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search, X, Gauge, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnly, formatDT } from '../../utils/timezone';

const CATEGORY_LABEL = { vehicle: 'Vehicle', trailer: 'Trailer', equipment: 'Equipment' };

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const ReadingField = ({ d, kind, value, onChange, error }) => {
  const isOdo = kind === 'odometer';
  const src = isOdo ? d.odometer_source : d.engine_hours_source;
  const current = isOdo ? d.computed_odometer : d.computed_engine_hours;
  const unit = isOdo ? 'km' : 'hrs';
  const editable = d.asset_editing_enabled && (isOdo ? src === 'gps' : src === 'ignition');
  const lastVal = isOdo ? d.last_service_odometer : d.last_service_hours;
  const lastDate = isOdo ? d.last_service_odometer_date : d.last_service_hours_date;
  const v = num(value);
  const delta = editable && v !== null && current != null ? v - current : null;
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{isOdo ? 'Odometer' : 'Engine hours'}</p>
      {src === 'off' ? (
        <p className="text-xs text-muted-foreground mt-1" data-testid={`bulk-readings-${kind}-off-${d.id}`}>Source off — not tracked</p>
      ) : src === 'sensor' ? (
        <p className="text-xs text-muted-foreground mt-1" data-testid={`bulk-readings-${kind}-sensor-${d.id}`}>
          {current != null ? `${Number(current).toLocaleString()} ${unit} · ` : ''}Sensor — set on device
        </p>
      ) : (
        <>
          <p className="text-xs text-sky-700 dark:text-sky-400" data-testid={`bulk-readings-${kind}-current-${d.id}`}>
            Currently: {current != null ? `${Number(current).toLocaleString()} ${unit}` : '—'}
          </p>
          <Input
            type="number" min="0" step="0.1"
            value={value}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value)}
            placeholder={editable ? `New ${unit} (blank = no change)` : 'Editing disabled'}
            className="h-8 text-sm mt-1"
            data-testid={`bulk-readings-${kind}-input-${d.id}`}
          />
          {delta !== null && (
            <p className={`text-[11px] mt-0.5 ${delta < 0 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid={`bulk-readings-${kind}-delta-${d.id}`}>
              {Number(current).toLocaleString()} → {v.toLocaleString()} ({delta >= 0 ? '+' : ''}{Math.round(delta * 10) / 10} {unit}){delta < 0 ? ' — lower than current' : ''}
            </p>
          )}
          {error && <p className="text-[11px] text-red-600 mt-0.5" data-testid={`bulk-readings-${kind}-error-${d.id}`}>{error}</p>}
        </>
      )}
      {lastVal != null && (
        <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5" data-testid={`bulk-readings-${kind}-lastservice-${d.id}`}>
          Last serviced at {Number(lastVal).toLocaleString()} {unit}{lastDate ? ` on ${formatDateOnly(lastDate)}` : ''}
        </p>
      )}
      {(isOdo ? d.last_correction_odometer_at : d.last_correction_hours_at) && (
        <p className="text-[10px] text-orange-600 dark:text-orange-400 mt-0.5" data-testid={`bulk-readings-${kind}-lastcorrection-${d.id}`}>
          Last corrected {formatDT(isOdo ? d.last_correction_odometer_at : d.last_correction_hours_at)}
          {(isOdo ? d.last_correction_odometer_by : d.last_correction_hours_by) ? ` by ${isOdo ? d.last_correction_odometer_by : d.last_correction_hours_by}` : ''}
        </p>
      )}
    </div>
  );
};

export const BulkReadingsDialog = ({ open, onOpenChange, onSaved, initialCategory = 'all' }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [edits, setEdits] = useState({}); // id -> { odometer: '', engine_hours: '' }
  const [rowErrors, setRowErrors] = useState({}); // id -> error string
  const [savedIds, setSavedIds] = useState(new Set());
  const [importReport, setImportReport] = useState(null);
  const fileRef = useRef(null);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fleet/devices/readings');
      setDevices(data.devices || []);
    } catch {
      toast.error('Could not load devices');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) {
      setEdits({}); setRowErrors({}); setSavedIds(new Set()); setSearch(''); setCategory(initialCategory || 'all'); setImportReport(null);
      fetchDevices();
    }
  }, [open, fetchDevices, initialCategory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter((d) => {
      if (category !== 'all' && d.category !== category) return false;
      if (q && !(`${d.label} ${d.registration}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [devices, search, category]);

  const items = useMemo(() => Object.entries(edits)
    .map(([id, e]) => ({
      device_id: id,
      odometer: num(e.odometer),
      engine_hours: num(e.engine_hours),
    }))
    .filter((i) => i.odometer !== null || i.engine_hours !== null), [edits]);
  const readingsCount = useMemo(() => items.reduce((n, i) => n + (i.odometer !== null ? 1 : 0) + (i.engine_hours !== null ? 1 : 0), 0), [items]);

  const setEdit = (id, field, value) => {
    setEdits((p) => ({ ...p, [id]: { ...(p[id] || { odometer: '', engine_hours: '' }), [field]: value } }));
  };

  const downloadTemplate = () => {
    const headers = ['IMEI', 'Vehicle', 'Registration', 'Odometer (km)', 'Engine Hours'];
    const lines = [headers.join(',')].concat(devices.map((d) =>
      [d.imei ? `="${d.imei}"` : '', `"${(d.label || '').replace(/"/g, '""')}"`, `"${d.registration || ''}"`, '', ''].join(',')
    ));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'readings_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const applyImport = (aoa) => {
    if (!aoa.length) { toast.error('The file is empty'); return; }
    const header = aoa[0].map((h) => String(h || '').toLowerCase());
    const col = (test) => header.findIndex(test);
    const iName = col((h) => h.includes('vehicle') || h === 'name' || h.includes('asset'));
    const iReg = col((h) => h.includes('registration') || h.includes('rego'));
    const iImei = col((h) => h.includes('imei'));
    const iOdo = col((h) => h.includes('odo'));
    const iHrs = col((h) => h.includes('hour') || h.includes('hrs'));
    if (iOdo === -1 && iHrs === -1) { toast.error('No "Odometer" or "Engine Hours" column found'); return; }
    if (iName === -1 && iReg === -1 && iImei === -1) { toast.error('Need a Vehicle, Registration or IMEI column to match devices'); return; }
    const byImei = {}; const byReg = {}; const byLabel = {};
    devices.forEach((d) => {
      if (d.imei) byImei[String(d.imei).trim()] = d;
      if (d.registration) byReg[d.registration.trim().toLowerCase()] = d;
      byLabel[(d.label || '').trim().toLowerCase()] = d;
    });
    const cleanNum = (v) => {
      const s = String(v ?? '').replace(/[, ]/g, '').trim();
      if (s === '') return null;
      const n = Number(s);
      return isNaN(n) || n < 0 ? undefined : n; // undefined = invalid
    };
    const cleanImei = (v) => String(v ?? '').replace(/[="']/g, '').trim();
    let filled = 0; const matchedIds = new Set(); const unmatched = []; const skipped = [];
    const newEdits = {};
    aoa.slice(1).forEach((row) => {
      if (!row.some((c) => String(c || '').trim() !== '')) return;
      const ident = (iImei !== -1 && cleanImei(row[iImei])) || (iReg !== -1 && String(row[iReg] || '').trim()) || (iName !== -1 && String(row[iName] || '').trim()) || 'row';
      const d = (iImei !== -1 && byImei[cleanImei(row[iImei])])
        || (iReg !== -1 && byReg[String(row[iReg] || '').trim().toLowerCase()])
        || (iName !== -1 && byLabel[String(row[iName] || '').trim().toLowerCase()]) || null;
      if (!d) { unmatched.push(ident); return; }
      matchedIds.add(d.id);
      const label = d.label;
      if (iOdo !== -1) {
        const v = cleanNum(row[iOdo]);
        if (v === undefined) skipped.push(`${label}: invalid odometer value`);
        else if (v !== null) {
          if (d.odometer_source === 'gps' && d.asset_editing_enabled) { newEdits[d.id] = { ...(newEdits[d.id] || {}), odometer: String(v) }; filled += 1; }
          else skipped.push(`${label}: odometer not app-editable (${d.asset_editing_enabled ? d.odometer_source : 'editing disabled'})`);
        }
      }
      if (iHrs !== -1) {
        const v = cleanNum(row[iHrs]);
        if (v === undefined) skipped.push(`${label}: invalid engine hours value`);
        else if (v !== null) {
          if (d.engine_hours_source === 'ignition' && d.asset_editing_enabled) { newEdits[d.id] = { ...(newEdits[d.id] || {}), engine_hours: String(v) }; filled += 1; }
          else skipped.push(`${label}: engine hours not app-editable (${d.asset_editing_enabled ? d.engine_hours_source : 'editing disabled'})`);
        }
      }
    });
    setEdits((p) => {
      const nx = { ...p };
      Object.entries(newEdits).forEach(([id, e]) => { nx[id] = { ...(nx[id] || { odometer: '', engine_hours: '' }), ...e }; });
      return nx;
    });
    setImportReport({ matched: matchedIds.size, filled, unmatched, skipped });
    if (filled) toast.success(`Pre-filled ${filled} reading${filled === 1 ? '' : 's'} — review, then Update`);
    else toast.warning('No readings could be pre-filled from that file');
  };

  const handleImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array', cellText: true, raw: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        applyImport(XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }));
      } catch {
        toast.error('Could not read that file — use .csv or .xlsx');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSave = async () => {
    if (!items.length) return;
    const bad = items.find((i) => (i.odometer !== null && (isNaN(i.odometer) || i.odometer < 0)) || (i.engine_hours !== null && (isNaN(i.engine_hours) || i.engine_hours < 0)));
    if (bad) { toast.error('Readings must be positive numbers'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/fleet/devices/bulk-readings', { items });
      const errs = {};
      const okIds = new Set(savedIds);
      (data.results || []).forEach((r) => {
        if (r.success) okIds.add(r.device_id);
        else errs[r.device_id] = r.error || 'Failed';
      });
      setRowErrors(errs);
      setSavedIds(okIds);
      if (data.failed === 0) {
        toast.success(`Updated readings on ${data.succeeded} device${data.succeeded === 1 ? '' : 's'}`);
        onSaved?.();
        onOpenChange(false);
      } else {
        toast.error(`${data.succeeded} updated, ${data.failed} failed — see rows for details`);
        // Clear inputs for the ones that saved so a retry only re-sends failures
        setEdits((p) => {
          const nx = { ...p };
          (data.results || []).forEach((r) => { if (r.success) delete nx[r.device_id]; });
          return nx;
        });
        onSaved?.();
        fetchDevices();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="bulk-readings-dialog">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2"><Gauge className="w-5 h-5" />Bulk Update Readings</DialogTitle>
          <DialogDescription>
            Set the current odometer / engine hours on many devices at once. Blank fields are left unchanged.
            Sensor-reported readings can only be set on the device itself.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or registration..." className="h-8 pl-9 pr-8" data-testid="bulk-readings-search" />
            {search && (
              <Button type="button" variant="ghost" size="sm" className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearch('')} data-testid="bulk-readings-search-clear">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-[150px]" data-testid="bulk-readings-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="vehicle">Vehicles</SelectItem>
              <SelectItem value="trailer">Trailers</SelectItem>
              <SelectItem value="equipment">Equipment</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8" onClick={() => fileRef.current?.click()} disabled={loading || devices.length === 0} data-testid="bulk-readings-import-btn">
            <Download className="w-4 h-4 mr-2" />
            Import
          </Button>
          <input
            ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); e.target.value = ''; }}
            data-testid="bulk-readings-import-file"
          />
          <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={downloadTemplate} disabled={loading || devices.length === 0} data-testid="bulk-readings-template-btn">
            Template
          </Button>
        </div>
        {importReport && (
          <div className="mx-6 mb-2 rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-1" data-testid="bulk-readings-import-report">
            <div className="flex items-center gap-2">
              <span className="font-medium">Import: {importReport.filled} reading{importReport.filled === 1 ? '' : 's'} pre-filled across {importReport.matched} device{importReport.matched === 1 ? '' : 's'}. Review below, then Update.</span>
              <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0 ml-auto shrink-0" onClick={() => setImportReport(null)} data-testid="bulk-readings-import-report-close">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            {importReport.unmatched.length > 0 && (
              <p className="text-amber-600 dark:text-amber-400" data-testid="bulk-readings-import-unmatched">
                Not matched ({importReport.unmatched.length}): {importReport.unmatched.slice(0, 5).join(', ')}{importReport.unmatched.length > 5 ? '…' : ''}
              </p>
            )}
            {importReport.skipped.length > 0 && (
              <p className="text-muted-foreground" data-testid="bulk-readings-import-skipped">
                Skipped ({importReport.skipped.length}): {importReport.skipped.slice(0, 5).join(' · ')}{importReport.skipped.length > 5 ? '…' : ''}
              </p>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-2" data-testid="bulk-readings-list">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="bulk-readings-empty">No devices match your filters.</p>
          )}
          {!loading && filtered.map((d) => {
            const e = edits[d.id] || { odometer: '', engine_hours: '' };
            return (
              <div key={d.id} className={`border rounded-md p-3 ${savedIds.has(d.id) ? 'border-emerald-400/60 bg-emerald-50/40 dark:bg-emerald-900/10' : ''}`} data-testid={`bulk-readings-row-${d.id}`}>
                <div className="flex items-center gap-2 mb-2 min-w-0">
                  <span className="text-sm font-medium truncate">{d.label}</span>
                  {d.registration && <span className="text-xs text-muted-foreground truncate">{d.registration}</span>}
                  <Badge variant="outline" className="text-[10px] shrink-0">{CATEGORY_LABEL[d.category]}</Badge>
                  {!d.asset_editing_enabled && <Badge variant="secondary" className="text-[10px] shrink-0">Editing disabled</Badge>}
                  {savedIds.has(d.id) && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 ml-auto" data-testid={`bulk-readings-saved-${d.id}`} />}
                </div>
                {rowErrors[d.id] && <p className="text-xs text-red-600 mb-2" data-testid={`bulk-readings-row-error-${d.id}`}>{rowErrors[d.id]}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ReadingField d={d} kind="odometer" value={e.odometer} onChange={(v) => setEdit(d.id, 'odometer', v)} />
                  <ReadingField d={d} kind="engine_hours" value={e.engine_hours} onChange={(v) => setEdit(d.id, 'engine_hours', v)} />
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="bulk-readings-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || items.length === 0} data-testid="bulk-readings-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Update {readingsCount} reading{readingsCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
